import React, { MouseEvent, useEffect, useMemo, useState, useRef } from "react";
// import CleverLogo from "../../assets/cleverlogo.jpeg";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { useLocation, useHistory } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import { ErrorType } from "../../../models/errors";
import {
  SAVE_INTEGRATION,
  GET_INTEGRATIONS,
  UPDATE_INTEGRATION,
  DELETE_INTEGRATION,
} from "./queries";
import { AppIntegration } from "../../../models/apps";
import { PaperSheet } from "../../common/ui_elements";
import { CopyButton } from "../../common/buttons";

const Applications: React.FC = () => {
  const query = useQueryURL();
  const history = useHistory();
  const location = useLocation();
  const [apps, setApps] = useState<AppIntegration[]>([]);
  const [activeApp, setActiveApp] = useState<AppIntegration | null>(null);
  const [error, setError] = useState<ErrorType | null>(null);
  const { data, refetch, loading } = useQuery(GET_INTEGRATIONS, {
    notifyOnNetworkStatusChange: true,
  });

  const [
    saveIntegration,
    { error: integrationError, loading: loadingSave, data: dataSave },
  ] = useMutation(SAVE_INTEGRATION);
  const [updateIntegration, { loading: loadingupdate }] =
    useMutation(UPDATE_INTEGRATION);
  const [
    deleteIntegration,
    { error: integrationDeleteError, loading: loadingRevoke },
  ] = useMutation(DELETE_INTEGRATION);

  useEffect(() => {
    const code = query.get("code");
    const error_url = query.get("error");
    const error_description = query.get("error_description");
    const response_type = query.get("response_type");
    const stored_app_id = localStorage.getItem("app_id");
    localStorage.removeItem("app_id");
    if (code) {
      // Determine the SSO type based on state parameter
      let ssoType = "edlink";
      if (response_type && response_type.includes("code")) {
        ssoType = "classlink";
      }

      saveIntegration({
        variables: {
          user_token: code,
          app_id: stored_app_id,
          sso_type: ssoType,
        },
      });
      removeQueryParams();
    } else if (error_url && error_description) {
      const set_error: ErrorType = {
        message: error_description,
      };
      setError(set_error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);
  useEffect(() => {
    if (integrationError) {
      console.log("applications.tsx:44 | integrationError", integrationError);
      const set_error: ErrorType = {
        message: integrationError.graphQLErrors[0].message,
      };
      setError(set_error);
    } else if (integrationDeleteError) {
      console.log(
        "applications.tsx:44 | integrationError",
        integrationDeleteError,
      );
      const set_error: ErrorType = {
        message: integrationDeleteError.graphQLErrors[0].message,
      };
      setError(set_error);
    }
  }, [integrationError, integrationDeleteError]);

  useEffect(() => {
    if (data) {
      setApps(data.userIntegrations);
      setActiveApp(data.userIntegrations.find((app: any) => app.active));
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (dataSave) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSave]);

  const removeQueryParams = () => {
    // Use the URLSearchParams API to work with query strings
    const queryParams = new URLSearchParams(location.search);

    // Remove the query parameter you don't want
    queryParams.delete("code");

    // Update the URL without a page refresh
    history.push(`${location.pathname}?${queryParams.toString()}`);
  };

  const handleEdlinkSignup = (app_id: string) => {
    localStorage.setItem("app_id", app_id);
    const loginUrl = `https://ed.link/sso/login?client_id=9aabc0db-fdeb-43e7-aadd-028467a96ac9&redirect_uri=${process.env.REACT_APP_URL}/settings/applications`;
    window.location.href = loginUrl;
  };

  const handleRevoke = async (app_id: string) => {
    if (loadingRevoke) {
      return;
    }
    await deleteIntegration({
      variables: {
        app_id,
      },
    });
    refetch();
  };

  const handleUpdate = async (app_id: string) => {
    if (loadingupdate) {
      return;
    }
    updateIntegration({
      variables: {
        app_id,
        import_students: true,
      },
    });
  };

  return (
    <div className="setting_panel applications_panel">
      <h4>Applications</h4>
      <p>
        When you link any of these apps, we'll gather and keep your student
        information up to date from the learning systems (LMS) you're connected
        to. Connect all that apply.
      </p>
      <div className="list">
        {error ? (
          <div className="alert alert-danger">
            <p>{error.message}</p>
          </div>
        ) : null}
        <ul className="list-group apps_list">
          {!loading && !loadingRevoke && !loadingSave ? (
            apps.length ? (
              apps.map((integration: any) => (
                <AppItem
                  key={`AppItem${integration.id}`}
                  id={integration.id}
                  handleClick={handleEdlinkSignup}
                  handleRevoke={handleRevoke}
                  handleUpdate={handleUpdate}
                  active={integration.active}
                  imgage={integration.logo}
                  title={integration.name}
                  loadingupdate={loadingupdate}
                  subtitle={integration.description}
                  url={{
                    name: integration.company,
                    url: integration.url,
                  }}
                />
              ))
            ) : (
              <ConnectLMS />
            )
          ) : (
            <RenderSkeleton />
          )}
        </ul>
        {activeApp ? <HelpComponent activeApp={activeApp} /> : null}
      </div>
    </div>
  );
};
type AppItemProps = {
  id: string;
  active: boolean;
  handleClick: any;
  handleRevoke: any;
  handleUpdate: any;
  imgage: string;
  loadingupdate: boolean;
  title: string;
  subtitle: string;
  url: {
    name: string;
    url: string;
  };
};
const AppItem: React.FC<AppItemProps> = ({
  id,
  active,
  handleClick,
  handleRevoke,
  handleUpdate,
  imgage,
  title,
  subtitle,
  loadingupdate,
  url,
}) => {
  const updateButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const button = updateButtonRef.current;
    if (button && !loadingupdate && active) {
      button.innerText = "Update";
      button.disabled = false;
    }
  }, [loadingupdate, active]);
  const runHandleUpdate = async (ev: MouseEvent<HTMLButtonElement>) => {
    if (loadingupdate || !updateButtonRef.current) {
      return;
    }
    // Update button text to "Updating.." and disable it
    updateButtonRef.current.innerText = "Updating...";
    updateButtonRef.current.disabled = true;

    handleUpdate(id);
  };
  return (
    <li className="list-group-item app_item ">
      <img src={imgage} alt={title} />
      <div>
        {title}{" "}
        {active ? (
          <small className="text-success">
            Connected <i className="fas fa-badge-check" aria-hidden="true"></i>
          </small>
        ) : null}{" "}
        <br />{" "}
        <small className="text-muted">
          {subtitle} · Owned by{" "}
          <a
            href={url.url}
            className="text-primary"
            target="_blank"
            rel="noopener noreferrer"
          >
            {url.name}
          </a>
          {active ? (
            <>
              {" "}
              ·{" "}
              <button
                className="btn btn-sm text-small text-danger m-0 p-0"
                onClick={() => handleRevoke(id)}
              >
                Revoke permissions
              </button>
            </>
          ) : null}
        </small>
      </div>
      <button
        onClick={active ? runHandleUpdate : () => handleClick(id)}
        ref={updateButtonRef}
        className={`btn ${
          active ? "btn-success" : "btn-outline-secondary"
        } btn-sm`}
      >
        {active ? "Update" : "Connect"}
      </button>
    </li>
  );
};
type HelpComponentProps = {
  activeApp: AppIntegration;
};
const HelpComponent: React.FC<HelpComponentProps> = ({ activeApp }) => {
  console.log("applications.tsx:290 | activeApp", activeApp);
  const signupUrl = `${process.env.REACT_APP_URL}/${activeApp.name === "google_classroom" ? "google" : "student_signup"}`;
  return (
    <>
      {" "}
      <h5 className="mt-4">
        Help your students sign up faster by sharing this link with them or
        posting it on your LMS.
      </h5>
      <p>
        <CopyButton classes="green_dark" text={signupUrl} showimg={true}>
          <span id="signupUrl">{signupUrl}</span>
        </CopyButton>
      </p>
      <PaperSheet copy={true}>
        {" "}
        Good day students, I have recently purchased a subscription to
        Flangoo.com, a Netflix-type site of digital readers.
        <br />
        <br />
        Please click the link below and log in with your <b>
          {activeApp.name}
        </b>{" "}
        credentials. <br />
        <span id="signupUrl">{signupUrl}</span> <br />
        <br />
        After you register, I will let you know what readers we will start
        reading soon.
        <br />
        Thank you.
      </PaperSheet>
    </>
  );
};

const ConnectLMS: React.FC = () => {
  const handleEdlinkSignup = (app_id: string) => {
    localStorage.setItem("app_id", app_id);
    const loginUrl = `https://ed.link/sso/login?client_id=9aabc0db-fdeb-43e7-aadd-028467a96ac9&redirect_uri=${process.env.REACT_APP_URL}/settings/applications`;
    window.location.href = loginUrl;
  };

  const handleClasslinkSignup = (app_id: string) => {
    localStorage.setItem("app_id", app_id);
    const clientId = process.env.REACT_APP_CLASS_LINK_CLIENT_ID;
    const redirectUri = `${process.env.REACT_APP_URL}/settings/applications`;
    const loginUrl = `https://launchpad.classlink.com/oauth2/v2/auth?response_type=code&client_id=${clientId}&redirect_uri=${encodeURIComponent(
      redirectUri,
    )}&scope=profile&state=teacher_applications`;
    window.location.href = loginUrl;
  };

  return (
    <>
      <li className="list-group-item app_item">
        <img
          src="https://flangoo-cdn.s3.us-east-2.amazonaws.com/canvaslogo.png"
          alt="Canvas"
        />
        <div>
          Canvas <br />{" "}
          <small className="text-muted">
            Learning Management System · Owned by{" "}
            <a
              href="https://www.instructure.com/"
              className="text-primary"
              target="_blank"
              rel="noopener noreferrer"
            >
              Instructure
            </a>
          </small>
        </div>
        <button
          onClick={() => handleEdlinkSignup("canvas")}
          className="btn btn-outline-secondary btn-sm"
        >
          Connect
        </button>
      </li>

      <li className="list-group-item app_item">
        <img
          src="https://filescdn.classlink.com/resources/icons/default/Classlink2.png"
          alt="ClassLink"
        />
        <div>
          ClassLink <br />{" "}
          <small className="text-muted">
            Single Sign-On Platform · Owned by{" "}
            <a
              href="https://www.classlink.com/"
              className="text-primary"
              target="_blank"
              rel="noopener noreferrer"
            >
              ClassLink
            </a>
          </small>
        </div>
        <button
          onClick={() => handleClasslinkSignup("classlink")}
          className="btn btn-outline-secondary btn-sm"
        >
          Connect
        </button>
      </li>

      <li className="list-group-item app_item">
        <img
          src="https://flangoo-cdn.s3.us-east-2.amazonaws.com/classroomlogo.svg"
          alt="Google Classroom"
        />
        <div>
          Google Classroom <br />{" "}
          <small className="text-muted">
            Learning Management System · Owned by{" "}
            <a
              href="https://classroom.google.com/"
              className="text-primary"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google
            </a>
          </small>
        </div>
        <button
          onClick={() => handleEdlinkSignup("google_classroom")}
          className="btn btn-outline-secondary btn-sm"
        >
          Connect
        </button>
      </li>

      <li className="list-group-item app_item">
        <img
          src="https://flangoo-cdn.s3.us-east-2.amazonaws.com/schoology.png"
          alt="Schoology"
        />
        <div>
          Schoology <br />{" "}
          <small className="text-muted">
            Learning Management System · Owned by{" "}
            <a
              href="https://www.powerschool.com/"
              className="text-primary"
              target="_blank"
              rel="noopener noreferrer"
            >
              PowerSchool
            </a>
          </small>
        </div>
        <button
          onClick={() => handleEdlinkSignup("schoology")}
          className="btn btn-outline-secondary btn-sm"
        >
          Connect
        </button>
      </li>

      <div className=" mt-3">
        <p className="m-0 small">
          {" "}
          Google Classroom, Schoology, and Canvas require administrator
          configuration. Click{" "}
          <Link to="/lms_help" className="text-primary text_underline">
            here
          </Link>{" "}
          to learn more.
        </p>
        <p className="text-dark m-0 small mt-2">
          Canvas, Schoology, and Google Classroom integrations require your
          district to allow Edlink tools in their LMS. Contact your district
          administrator if you encounter any issues.
        </p>
      </div>
    </>
  );
};

const RenderSkeleton: React.FC = () => {
  const Item = () => (
    <li className="list-group-item app_item ">
      <div className="row">
        <div className="col-2">
          <Skeleton height="70px" />
        </div>
        <div className="col-6">
          <Skeleton width="40%" />
          <br />{" "}
          <small className="text-muted">
            <Skeleton count={2} />
          </small>
        </div>
      </div>
    </li>
  );
  const items = [1, 2, 3];
  return (
    <div>
      {items.map((item) => (
        <Item key={`item${item}`} />
      ))}
    </div>
  );
};

function useQueryURL() {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
}

export default Applications;
